"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSensorUsageDaily = void 0;
const react_1 = require("react");
const queries_1 = require("../../graphql/queries");
const amplifyBootstrap_1 = require("../../utils/amplifyBootstrap");
/**
 * Hook to fetch hourly sensor usage data for a specific sensor on a specific date
 * @param params - Parameters for fetching sensor usage data
 * @returns Sensor usage data by hour, loading state, error, and refetch function
 */
const useSensorUsageDaily = ({ sensorId, date, enabled = true, }) => {
    const [data, setData] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const fetchSensorUsageData = async () => {
        if (!enabled || !sensorId || !date) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            // Since we want data for a specific day, use the same date for start and end
            const result = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorUsageRollup,
                variables: {
                    sensorId,
                    startDate: date,
                    endDate: date,
                    nextToken: undefined, // For now, we'll handle pagination later if needed
                },
            });
            const rawData = result?.data?.getSensorUsageRollup;
            const sensorUsageData = rawData?.items || rawData || [];
            if (sensorUsageData && Array.isArray(sensorUsageData) && sensorUsageData.length > 0) {
                // Map SensorUsageRollup to SensorHourlyDataPoint format
                const hourlyDataPoints = sensorUsageData.map((item) => {
                    // Create base hourly data point
                    const dataPoint = {
                        sensorId: item.sensorId,
                        dateHour: item.dateHour || `${item.date}-00`,
                        date: item.date,
                        hour: item.hour !== undefined ? item.hour : 0,
                        totalDataPoints: item.totalDataPoints,
                        sensorType: item.sensorType || 'UNKNOWN',
                        hubId: item.hubId,
                        createdAt: item.createdAt,
                        updatedAt: item.updatedAt,
                    };
                    // We're no longer adding sensor-specific data to the base data point
                    // This keeps the hook generic for all sensor types
                    return dataPoint;
                });
                // Sort by hour in ascending order for consistent data processing
                const sortedData = hourlyDataPoints.sort((a, b) => a.hour - b.hour);
                setData(sortedData);
            }
            else {
                setData([]);
            }
        }
        catch (err) {
            console.error('Error fetching sensor usage data:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch sensor usage data');
            setData(null);
        }
        finally {
            setLoading(false);
        }
    };
    const refetch = async () => {
        await fetchSensorUsageData();
    };
    (0, react_1.useEffect)(() => {
        fetchSensorUsageData();
    }, [sensorId, date, enabled]);
    return {
        data,
        loading,
        error,
        refetch,
    };
};
exports.useSensorUsageDaily = useSensorUsageDaily;
